import React, { useEffect, useMemo, useState } from 'react';
import { isIosSafari } from '../pwa/platformDetection';
import { isStandaloneMode, shouldEnableAndroidInstallHook, useAndroidDeferredInstallPrompt } from '../pwa/installDetection';

const DISMISSED_AT_KEY = 'choruss_pwa_prompt_dismissed_at';
const DISMISSED_SESSION_KEY = 'choruss_pwa_prompt_dismissed_session';
const DISMISS_COOLDOWN_MS = 60 * 60 * 1000; // 1 hour

function readDismissedAt(): number | null {
  try {
    const raw = window.localStorage.getItem(DISMISSED_AT_KEY);
    if (!raw) return null;
    const asNum = Number(raw);
    if (!Number.isFinite(asNum)) return null;
    return asNum;
  } catch {
    return null;
  }
}

function markDismissedNow() {
  try {
    const now = Date.now();
    window.localStorage.setItem(DISMISSED_AT_KEY, String(now));
    window.sessionStorage.setItem(DISMISSED_SESSION_KEY, '1');
  } catch {
    // ignore storage failures
  }
}

export default function PWAInstallPrompt() {
  const androidInstall = useAndroidDeferredInstallPrompt({
    enabled: shouldEnableAndroidInstallHook(),
  });

  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const [iosGuideOpen, setIosGuideOpen] = useState(false);

  const isIos = useMemo(() => (typeof window !== 'undefined' ? isIosSafari() : false), []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (isStandaloneMode()) {
      setMounted(false);
      setVisible(false);
      setIosGuideOpen(false);
      return;
    }

    const dismissedThisSession = window.sessionStorage.getItem(DISMISSED_SESSION_KEY) === '1';
    if (dismissedThisSession) {
      setMounted(false);
      setVisible(false);
      setIosGuideOpen(false);
      return;
    }

    const dismissedAt = readDismissedAt();
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_COOLDOWN_MS) {
      setMounted(false);
      setVisible(false);
      setIosGuideOpen(false);
      return;
    }

    const shouldShow = isIos || androidInstall.canPrompt;
    if (!shouldShow) {
      setMounted(false);
      setVisible(false);
      setIosGuideOpen(false);
      return;
    }

    setMounted(true);
  }, [androidInstall.canPrompt, isIos]);

  useEffect(() => {
    if (!mounted) return;

    const t = window.setTimeout(() => setVisible(true), 150);
    return () => window.clearTimeout(t);
  }, [mounted]);

  const dismiss = () => {
    markDismissedNow();
    setVisible(false);
    setIosGuideOpen(false);
    window.setTimeout(() => setMounted(false), 520);
  };

  const handleAdd = async () => {
    if (isIos) {
      setIosGuideOpen(true);
      return;
    }

    const result = await androidInstall.promptInstall();
    if (result.outcome === 'dismissed') {
      dismiss();
      return;
    }
    if (result.outcome === 'accepted') {
      setVisible(false);
      window.setTimeout(() => setMounted(false), 520);
    }
  };

  if (!mounted) return null;

  return (
    <>
      <div
        className={[
          'fixed top-4 left-0 right-0 z-50 w-[95%] max-w-md mx-auto',
          'transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)]',
          visible ? 'translate-y-0 opacity-100' : '-translate-y-6 opacity-0 pointer-events-none',
        ].join(' ')}
        role="region"
        aria-label="Install Choruss app"
      >
        <div className="bg-white rounded-full shadow-[0_8px_30px_rgba(0,0,0,0.12)] border border-gray-100">
          <div className="flex items-center gap-3 px-4 py-2">
            <div className="shrink-0 text-yellow-500 text-lg" aria-hidden>
              ⚡
            </div>

            <div className="flex-1 min-w-0 text-xs sm:text-sm leading-snug text-gray-900">
              Never miss your events, add <span className="font-bold">Choruss</span> to your Home Screen!
            </div>

            <button
              type="button"
              onClick={handleAdd}
              className="shrink-0 bg-blue-600 text-white rounded-full px-5 py-1.5 font-bold text-xs sm:text-sm whitespace-nowrap shadow-sm active:scale-[0.98] transition-transform"
            >
              Add
            </button>

            <button
              type="button"
              onClick={dismiss}
              className="shrink-0 text-gray-400 hover:text-gray-600 px-1"
              aria-label="Dismiss install prompt"
            >
              ×
            </button>
          </div>
        </div>
      </div>

      {iosGuideOpen && (
        <div className="fixed inset-0 z-50" role="dialog" aria-label="How to add to Home Screen">
          <button
            type="button"
            className="absolute inset-0 bg-black/30 backdrop-blur-[2px]"
            aria-label="Close guide"
            onClick={() => setIosGuideOpen(false)}
          />

          <div className="absolute bottom-0 left-0 right-0 p-4">
            <div className="mx-auto max-w-md rounded-2xl bg-white border border-gray-100 shadow-[0_12px_40px_rgba(0,0,0,0.20)] p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-bold text-gray-900">Add to Home Screen</div>
                  <div className="text-xs text-gray-500 mt-0.5">iOS Safari steps</div>
                </div>
                <button
                  type="button"
                  className="text-gray-400 hover:text-gray-600 px-2 -mr-2"
                  aria-label="Close guide"
                  onClick={() => setIosGuideOpen(false)}
                >
                  ×
                </button>
              </div>

              <ol className="mt-3 space-y-2 text-sm text-gray-700">
                <li className="flex gap-2">
                  <span className="font-semibold text-gray-900">1.</span>
                  <span>
                    Tap the <span className="font-semibold">Share</span> icon (square with the up arrow).
                  </span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-gray-900">2.</span>
                  <span>
                    Scroll and select <span className="font-semibold">Add to Home Screen</span>.
                  </span>
                </li>
              </ol>

              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  className="bg-gray-900 text-white rounded-full px-4 py-2 text-sm font-bold"
                  onClick={() => setIosGuideOpen(false)}
                >
                  Got it
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

